import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import { queryWidget } from './api.js';
import { datasourceKey, isOwnCrossFilter, queryKeyFor } from './constants.js';

const EMPTY_ROWS = [];
const EMPTY_COLS = [];
const EMPTY_CROSS_FILTERS = {};

// Cross filters set by filter widgets for this datasource (except the widget's own), combined with AND
export const selectCrossFilter = (datasourceId, widgetId) => (state) => {
  const models = state.datasources.datasources[datasourceKey(datasourceId)]?.crossFilterModels ?? EMPTY_CROSS_FILTERS;
  const conditions = Object.entries(models)
    .filter(([filterId, model]) => model && !isOwnCrossFilter(filterId, widgetId))
    .map(([, model]) => model);
  if (conditions.length === 0) return null;
  if (conditions.length === 1) return conditions[0];
  return { filterType: 'join', type: 'AND', conditions };
};

/**
 * Runs the widget's saved v2 query. The server applies the stored query spec; only the
 * cross filter is sent from the browser. Status is 'loading' until the datasource has data.
 *
 * @param {number} widgetId
 * @param {boolean} [options.enabled] false for widgets that load their own rows (paged grids)
 */
export default function useDatasourceQuery(widgetId, { enabled = true } = {}) {
  const dashboardId = useSelector((state) => state.dashboard.dashboardId);
  const datasourceId = useSelector((state) => state.dashboard.widgets[widgetId]?.datasource_v2_id);
  const savedQuery = useSelector((state) => state.dashboard.widgets[widgetId]?.datasource_query);
  const settingsColumn = useSelector((state) => state.dashboard.widgets[widgetId]?.settings?.columnId);
  const crossFilter = useSelector(selectCrossFilter(datasourceId, widgetId), isEqual);

  const { data, isLoading, error } = useQuery({
    // settingsColumn: button filters derive their query from it server side
    queryKey: queryKeyFor(datasourceId, widgetId, savedQuery, settingsColumn, crossFilter),
    queryFn: () => queryWidget(dashboardId, widgetId, { runtime: { filter: crossFilter } }),
    enabled: enabled && datasourceId != null && dashboardId != null,
    placeholderData: keepPreviousData,
    retry: (count, err) => count < 2 && !(err?.status >= 400 && err?.status < 500),
  });

  let status = 'ready';
  if (error) status = 'error';
  else if (isLoading || data?.status === 'loading') status = 'loading';
  else if (data?.status === 'failed') status = 'failed';

  return {
    datasourceId,
    rows: data?.rows ?? EMPTY_ROWS,
    cols: data?.cols ?? EMPTY_COLS,
    values: data?.values,
    truncated: !!data?.truncated,
    meta: data?.meta,
    status: enabled ? status : 'ready',
    error: error?.body?.errors,
  };
}
